import { AsyncPipe, DecimalPipe, NgClass } from '@angular/common'
import { Component, inject, OnDestroy, OnInit } from '@angular/core'
import { ActivatedRoute, Router, RouterLink } from '@angular/router'

import { TranslatePipe } from '@ngx-translate/core'
import { Button } from 'primeng/button'
import { Skeleton } from 'primeng/skeleton'
import {
  BehaviorSubject,
  combineLatest,
  firstValueFrom,
  Observable,
  switchMap,
  takeUntil,
} from 'rxjs'

import { Action, LanguageCode, PricingPlans, Resource } from '@ti/contracts'
import { ApiService } from '@ti/core/api'
import { Memoize } from '@ti/core/common/decorators'
import { injectDestroy$, selectState } from '@ti/core/common/utils'
import { ArrowIconComponent } from '@ti/core/icons/arrow'
import { BillingIconComponent } from '@ti/core/icons/billing'
import { DeviceIconComponent } from '@ti/core/icons/device'
import { InfoIconComponent } from '@ti/core/icons/info'
import { PayDisableIconComponent } from '@ti/core/icons/pay-disable'
import { DateFormatService, LanguageService } from '@ti/core/ui-kit/i18n'
import { FormatDatePipe } from '@ti/core/ui-kit/i18n/pipes'
import { CounterComponent } from '@ti/core/ui-kit/layout/components/counter'
import { DataGridComponent } from '@ti/core/ui-kit/layout/components/data-grid'
import { FleetHeaderComponent } from '@ti/core/ui-kit/layout/components/fleet-header'
import { TooltipDirective } from '@ti/core/ui-kit/layout/directives'

import { BillingUsageReportModel } from '../../models/billing-usage-report.model'

@Component({
  selector: 'app-tsp-admin-usage-report',
  templateUrl: 'billing-usage-report.component.html',
  styleUrls: ['billing-usage-report.component.scss'],
  providers: [BillingUsageReportModel],
  imports: [
    Skeleton,
    AsyncPipe,
    FleetHeaderComponent,
    CounterComponent,
    TranslatePipe,
    DeviceIconComponent,
    InfoIconComponent,
    TooltipDirective,
    RouterLink,
    DecimalPipe,
    NgClass,
    DataGridComponent,
    BillingIconComponent,
    FormatDatePipe,
    PayDisableIconComponent,
    Button,
    ArrowIconComponent,
  ],
})
export class BillingUsageReportComponent implements OnInit, OnDestroy {
  // Dependencies
  protected api = inject(ApiService)
  protected readonly router = inject(Router)
  protected readonly languageService = inject(LanguageService)
  protected readonly activatedRoute = inject(ActivatedRoute)
  protected readonly dateFormatService = inject(DateFormatService)
  protected readonly destroy$ = injectDestroy$()
  // State
  protected readonly model = inject(BillingUsageReportModel)
  protected readonly state = selectState(BillingUsageReportModel)
  protected readonly tspName$ = new BehaviorSubject<string>('')
  protected readonly invoiceDate$ = new BehaviorSubject<Date>(new Date())
  protected readonly pageLoading$ = new BehaviorSubject<boolean>(true)

  // Enums
  protected readonly LanguageCode = LanguageCode
  protected readonly Resource = Resource
  protected readonly Action = Action
  protected readonly PricingPlans = PricingPlans

  @Memoize()
  get title(): Observable<string> {
    return combineLatest([this.invoiceDate$, this.tspName$]).pipe(
      switchMap(async ([invoiceDate, tspName]) => {
        const formattedDate = await this.dateFormatService.formatDate(invoiceDate, 'month-year')
        return `${tspName} [${formattedDate}]`
      }),
    )
  }

  async ngOnInit() {
    const params = await firstValueFrom(this.activatedRoute.paramMap)

    try {
      const tspId = parseInt(params.get('tspId') ?? '')
      const year = params.get('year')
      const month = params.get('month')

      if (!tspId || !year || !month) {
        return this.goBack()
      }

      const tsp = await this.api.ti.accounts.one(tspId)
      const parsedYear = parseInt(year, 10)
      const parsedMonth = parseInt(month, 10) - 1 // Convert to zero-based month index

      this.model.tspId = tspId
      this.model.year = parsedYear
      this.model.month = parsedMonth
      this.model.setAccountBillingMode(tsp.billingMode)
      // Create invoice date in local time-zone for the first day of the specified month and year
      this.invoiceDate$.next(new Date(parsedYear, parsedMonth, 1))
      this.tspName$.next(tsp.name)
      await this.model.load()

      this.state.isLoading$.pipe(takeUntil(this.destroy$)).subscribe((loading) => {
        if (!loading) {
          setTimeout(() => this.pageLoading$.next(false), 300)
        }
      })
    } catch (e) {
      console.error('Billing Usage Error: ', e, params)
      return this.goBack()
    }
  }

  ngOnDestroy() {
    this.model.clearHistory()
  }

  goBack() {
    return this.router.navigate(['/billing/history'])
  }
}
